import React, { useState, useEffect, useCallback } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SvgElement, icontypesEnum } from '../assets/svgElement';
import LoanCleanupPanel from './LoanCleanupPanel';

const formatAmount = (amount: any) => '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

const statusBadge = (s: string) => {
    switch (s) {
        case 'active': return 'bg-blue-100 text-blue-700';
        case 'completed': return 'bg-green-100 text-green-700';
        case 'cancelled': return 'bg-gray-200 text-gray-600';
        default: return 'bg-gray-100 text-gray-700';
    }
};

const money = (v: any) => `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function RiderLoans() {
    const [loans, setLoans] = useState<any[]>([]);
    const [pagination, setPagination] = useState<any>({ current_page: 1, last_page: 1 });
    const [loader, setLoader] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);

    // Filters
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');

    // Record modal
    const [recordModal, setRecordModal] = useState(false);
    const [riderSearch, setRiderSearch] = useState('');
    const [riderResults, setRiderResults] = useState<any[]>([]);
    const [selectedRider, setSelectedRider] = useState<any>(null);
    const [principal, setPrincipal] = useState('');
    const [addInterest, setAddInterest] = useState(false);
    const [interestRate, setInterestRate] = useState('');
    const [chargeCycle, setChargeCycle] = useState('weekly');
    const [chargeAmount, setChargeAmount] = useState('');
    const [disbursedAt, setDisbursedAt] = useState('');
    const [notes, setNotes] = useState('');

    // Detail modal
    const [detailModal, setDetailModal] = useState(false);
    const [detail, setDetail] = useState<any>(null);

    // Edit modal
    const [editModal, setEditModal] = useState(false);
    const [editLoan, setEditLoan] = useState<any>(null);
    const [editPrincipal, setEditPrincipal] = useState('');
    const [editAddInterest, setEditAddInterest] = useState(false);
    const [editInterestRate, setEditInterestRate] = useState('');
    const [editChargeCycle, setEditChargeCycle] = useState('weekly');
    const [editChargeAmount, setEditChargeAmount] = useState('');
    const [editNextChargeDate, setEditNextChargeDate] = useState('');
    const [editNotes, setEditNotes] = useState('');

    const loadLoans = useCallback((page?: number) => {
        setLoader(true);
        const params: any = {
            search: search || undefined,
            status: status !== 'all' ? status : undefined,
            page: page || 1,
            per_page: 15,
        };
        SuperAdminApis.getRiderLoans(params).then((res: AxiosResponse<any>) => {
            setLoader(false);
            if (res?.data?.status) {
                setLoans(res.data.data.loans || []);
                setPagination(res.data.data.pagination || { current_page: 1, last_page: 1 });
            }
        }).catch(() => { setLoader(false); toast.error('Failed to load loans.'); });
    }, [search, status]);

    useEffect(() => { loadLoans(); }, []);

    // Rider search (debounced-ish on submit)
    const searchRiders = (term: string) => {
        setRiderSearch(term);
        if (term.length < 2) { setRiderResults([]); return; }
        // Server-side, on purpose: eligibility means "has a wallet money actually
        // reaches", and only the server knows which wallet that is — a rider under
        // a fleet repays from the fleet agent's, a legacy duplicate from none.
        SuperAdminApis.getEligibleLoanRiders(term).then((res: AxiosResponse<any>) => {
            const list = res?.data?.data ?? [];
            setRiderResults(Array.isArray(list) ? list : []);
        }).catch(() => setRiderResults([]));
    };

    // Client-side preview of interest / cycles / payoff
    const preview = (() => {
        const p = parseFloat(principal), c = parseFloat(chargeAmount);
        const rate = addInterest ? (parseFloat(interestRate) || 0) : 0;
        const interest = +(p * rate / 100).toFixed(2);
        const total = +(p + interest).toFixed(2);
        if (!p || !c || c <= 0 || c > total) return null;
        const cycles = Math.ceil(total / c);
        const days = chargeCycle === 'bi_weekly' ? 14 : 7;
        const start = disbursedAt ? new Date(disbursedAt) : new Date();
        const payoff = new Date(start.getTime());
        payoff.setDate(payoff.getDate() + cycles * days);
        return { cycles, payoff: payoff.toLocaleDateString(), last: (total - (cycles - 1) * c), interest, total };
    })();

    const resetRecord = () => {
        setSelectedRider(null); setRiderSearch(''); setRiderResults([]);
        setPrincipal(''); setAddInterest(false); setInterestRate(''); setChargeCycle('weekly'); setChargeAmount(''); setDisbursedAt(''); setNotes('');
    };

    const submitLoan = () => {
        if (!selectedRider) { toast.error('Please select a rider.'); return; }
        if (!principal || parseFloat(principal) <= 0) { toast.error('Enter a valid loan amount.'); return; }
        if (!chargeAmount || parseFloat(chargeAmount) <= 0) { toast.error('Enter a valid charge amount.'); return; }
        if (parseFloat(chargeAmount) > parseFloat(principal)) { toast.error('Charge amount cannot exceed the loan amount.'); return; }

        setActionLoader(true);
        SuperAdminApis.recordRiderLoan({
            user_id: selectedRider.id,
            principal_amount: parseFloat(principal),
            interest_rate: addInterest ? (parseFloat(interestRate) || 0) : 0,
            charge_cycle: chargeCycle,
            charge_amount: parseFloat(chargeAmount),
            disbursed_at: disbursedAt || undefined,
            notes: notes || undefined,
        }).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            if (res?.data?.status) {
                toast.success(res.data.message || 'Loan recorded.');
                setRecordModal(false); resetRecord(); loadLoans();
            } else {
                toast.error(res?.data?.message || 'Failed to record loan.');
            }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Failed to record loan.');
        });
    };

    const viewDetail = (id: number) => {
        setDetail(null); setDetailModal(true);
        SuperAdminApis.getRiderLoanDetail(id).then((res: AxiosResponse<any>) => {
            if (res?.data?.status) setDetail(res.data.data);
        }).catch(() => toast.error('Failed to load loan detail.'));
    };

    const openEdit = (l: any) => {
        setEditLoan(l);
        setEditPrincipal(String(l.principal_amount ?? ''));
        setEditAddInterest(Number(l.interest_rate) > 0);
        setEditInterestRate(Number(l.interest_rate) > 0 ? String(l.interest_rate) : '');
        setEditChargeCycle(l.charge_cycle || 'weekly');
        setEditChargeAmount(String(l.charge_amount ?? ''));
        setEditNextChargeDate(l.next_charge_date || '');
        setEditNotes(l.notes || '');
        setEditModal(true);
    };

    const editHasCharges = Number(editLoan?.cycles_charged ?? 0) > 0;

    // Live preview when editing a loan that hasn't been charged yet.
    const editPreview = (() => {
        if (!editLoan || editHasCharges) return null;
        const p = parseFloat(editPrincipal), c = parseFloat(editChargeAmount);
        const rate = editAddInterest ? (parseFloat(editInterestRate) || 0) : 0;
        const interest = +(p * rate / 100).toFixed(2);
        const total = +(p + interest).toFixed(2);
        if (!p || !c || c <= 0 || c > total) return null;
        return { cycles: Math.ceil(total / c), interest, total };
    })();

    const submitEdit = () => {
        if (!editLoan) return;
        if (!editHasCharges && (!editPrincipal || parseFloat(editPrincipal) <= 0)) { toast.error('Enter a valid loan amount.'); return; }
        if (!editChargeAmount || parseFloat(editChargeAmount) <= 0) { toast.error('Enter a valid charge amount.'); return; }

        setActionLoader(true);
        SuperAdminApis.updateRiderLoan(editLoan.id, {
            principal_amount: parseFloat(editPrincipal),
            interest_rate: editAddInterest ? (parseFloat(editInterestRate) || 0) : 0,
            charge_cycle: editChargeCycle,
            charge_amount: parseFloat(editChargeAmount),
            next_charge_date: editNextChargeDate || undefined,
            notes: editNotes || undefined,
        }).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            if (res?.data?.status) {
                toast.success(res.data.message || 'Loan updated.');
                setEditModal(false); loadLoans(pagination.current_page);
            } else {
                toast.error(res?.data?.message || 'Failed to update loan.');
            }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Failed to update loan.');
        });
    };

    const cancelLoan = (id: number) => {
        if (!window.confirm('Cancel this loan? Future charges will stop. Past charges are not reversed.')) return;
        SuperAdminApis.cancelRiderLoan(id).then((res: AxiosResponse<any>) => {
            if (res?.data?.status) { toast.success('Loan cancelled.'); loadLoans(pagination.current_page); }
            else { toast.error(res?.data?.message || 'Failed to cancel.'); }
        }).catch((err: any) => toast.error(err?.response?.data?.message || 'Failed to cancel.'));
    };

    return (
        <>
            <AdminSidebar title="Rider Loans" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Rider Loans" />
                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">
                    <div className="flex justify-between items-center mt-5 mb-2">
                        <div>
                            <h3 className="text-[15px] text-[#1C1C1C] font-semibold">Rider Loans</h3>
                            <p className="text-xs text-gray-500">Record loans and track repayments deducted from rider earnings each cycle.</p>
                        </div>
                        <button onClick={() => { resetRecord(); setRecordModal(true); }} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">+ Record Loan</button>
                    </div>

                    <LoanCleanupPanel onApplied={loadLoans} />

                    {/* Filters */}
                    <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <div className="flex-1 min-w-[150px]">
                                <label className="text-xs text-gray-500">Search rider</label>
                                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, phone..." className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="min-w-[130px]">
                                <label className="text-xs text-gray-500">Status</label>
                                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                    <option value="all">All</option>
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                            <button onClick={() => loadLoans()} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Filter</button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="relative overflow-x-auto">
                        {loader && <div className="flex justify-center py-6"><Oval visible height="30" width="30" color="#E04343" secondaryColor="#eee" ariaLabel="loading" /></div>}
                        <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                            <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                                <tr>
                                    <th className="px-2 py-2">Rider</th>
                                    <th className="px-2 py-2">Principal</th>
                                    <th className="px-2 py-2">Charge</th>
                                    <th className="px-2 py-2">Progress</th>
                                    <th className="px-2 py-2">Outstanding</th>
                                    <th className="px-2 py-2">Next charge</th>
                                    <th className="px-2 py-2">Status</th>
                                    <th className="px-4 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loans.length > 0 ? loans.map((l) => (
                                    <tr key={l.id} className="bg-white">
                                        <td className="px-2 py-3">
                                            <div className="text-xs font-semibold">{l.rider_name || '—'}</div>
                                            <div className="text-[10px] text-gray-400">{l.rider_email}</div>
                                        </td>
                                        <td className="px-2 py-3 text-xs font-semibold">{formatAmount(l.principal_amount)}{l.interest_rate > 0 && <div className="text-[10px] text-amber-600 font-normal">+{l.interest_rate}% → {formatAmount(l.total_repayable)}</div>}</td>
                                        <td className="px-2 py-3 text-xs">{formatAmount(l.charge_amount)}<div className="text-[10px] text-gray-400">{l.charge_cycle === 'bi_weekly' ? 'bi-weekly' : 'weekly'}</div></td>
                                        <td className="px-2 py-3 text-xs">
                                            {l.cycles_charged}/{l.total_cycles} cycles
                                            <div className="text-[10px] text-gray-400">{formatAmount(l.amount_repaid)} repaid</div>
                                        </td>
                                        <td className="px-2 py-3 text-xs font-semibold">{formatAmount(l.outstanding_balance)}</td>
                                        <td className="px-2 py-3 text-xs">{l.next_charge_date || '—'}</td>
                                        <td className="px-2 py-3"><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${statusBadge(l.status)}`}>{l.status}</span></td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                <button onClick={() => viewDetail(l.id)} className="text-blue-600 hover:underline text-xs font-semibold">View</button>
                                                {l.status === 'active' && <button onClick={() => openEdit(l)} className="text-emerald-600 hover:underline text-xs font-semibold">Edit</button>}
                                                {l.status === 'active' && <button onClick={() => cancelLoan(l.id)} className="text-red-600 hover:underline text-xs font-semibold">Cancel</button>}
                                            </div>
                                        </td>
                                    </tr>
                                )) : !loader && (
                                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">No loans found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.last_page > 1 && (
                        <div className="flex justify-end mt-4 gap-1">
                            <button disabled={pagination.current_page <= 1} onClick={() => loadLoans(pagination.current_page - 1)} className="py-1 px-3 text-xs bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 rounded">Prev</button>
                            <span className="py-1 px-3 text-xs">Page {pagination.current_page} of {pagination.last_page}</span>
                            <button disabled={pagination.current_page >= pagination.last_page} onClick={() => loadLoans(pagination.current_page + 1)} className="py-1 px-3 text-xs bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 rounded">Next</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Record Loan Modal */}
            <Modal visible={recordModal} width="480" height="600" effect="fadeInUp" onClickAway={() => setRecordModal(false)}>
                <div className="p-5 overflow-y-auto max-h-[590px]">
                    <div className="flex justify-between mb-4">
                        <p className="font-bold">Record a Loan</p>
                        <p className="cursor-pointer" onClick={() => setRecordModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>

                    {/* Rider picker */}
                    <div className="mb-3">
                        <label className="text-xs text-gray-600 mb-1 block">Rider</label>
                        {selectedRider ? (
                            <div className="border border-gray-300 rounded-md p-2 text-sm bg-gray-50">
                                <div className="flex justify-between items-center">
                                    <span>{selectedRider.name} <span className="text-gray-400 text-xs">({selectedRider.email})</span></span>
                                    <button onClick={() => setSelectedRider(null)} className="text-red-500 text-xs">change</button>
                                </div>
                                {/* Said before the loan is saved, because it is the thing that went
                                    wrong before: which wallet the repayments will come out of. */}
                                <p className="text-[11px] text-gray-600 mt-1">
                                    Repayments will be deducted from the <strong>{selectedRider.charges}</strong>
                                    {' '}— balance {money(selectedRider.wallet_balance)}, earned {money(selectedRider.wallet_earned)} to date.
                                </p>
                            </div>
                        ) : (
                            <>
                                <input type="text" value={riderSearch} onChange={(e) => searchRiders(e.target.value)} placeholder="Search rider by name / email..." className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                <p className="text-[10px] text-gray-400 mt-1">
                                    Only accounts with an active wallet that has received earnings are shown. A rider under
                                    a fleet repays from the fleet agent's wallet.
                                </p>
                                {riderSearch.length >= 2 && riderResults.length === 0 && (
                                    <p className="text-[11px] text-amber-700 mt-1">
                                        No eligible account matches. If this rider exists but is missing here, their account
                                        has no wallet with earnings — they may be on a legacy duplicate.
                                    </p>
                                )}
                                {riderResults.length > 0 && (
                                    <div className="border border-gray-200 rounded-md mt-1 max-h-48 overflow-y-auto">
                                        {riderResults.map((r) => (
                                            <div key={r.id}
                                                onClick={() => { if (r.has_active_loan) return; setSelectedRider(r); setRiderResults([]); setRiderSearch(''); }}
                                                className={`p-2 text-xs border-b last:border-b-0 ${r.has_active_loan ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'}`}
                                                title={r.has_active_loan ? 'Already has an active loan' : ''}>
                                                <div className="flex justify-between gap-2">
                                                    <span>{r.name} <span className="text-gray-400">({r.email}{r.phone ? ` · ${r.phone}` : ''})</span></span>
                                                    {r.has_active_loan && <span className="text-[10px] text-amber-700">active loan</span>}
                                                </div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">
                                                    Charges the {r.charges} · balance {money(r.wallet_balance)} · earned {money(r.wallet_earned)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="mb-3">
                        <label className="text-xs text-gray-600 mb-1 block">Loan amount (₦)</label>
                        <input type="number" value={principal} onChange={(e) => setPrincipal(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>

                    {/* Interest (optional) */}
                    <div className="mb-3">
                        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={addInterest} onChange={(e) => { setAddInterest(e.target.checked); if (!e.target.checked) setInterestRate(''); }} />
                            Add interest to this loan
                        </label>
                        {addInterest && (
                            <div className="mt-2">
                                <label className="text-xs text-gray-600 mb-1 block">Interest rate (% of loan amount)</label>
                                <input type="number" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} placeholder="e.g. 10" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                {preview && preview.interest > 0 && (
                                    <p className="text-[10px] text-gray-500 mt-1">Interest: {formatAmount(preview.interest)} → Total repayable: <strong>{formatAmount(preview.total)}</strong></p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">Charge cycle</label>
                            <select value={chargeCycle} onChange={(e) => setChargeCycle(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                <option value="weekly">Weekly</option>
                                <option value="bi_weekly">Bi-weekly</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">Amount per charge (₦)</label>
                            <input type="number" value={chargeAmount} onChange={(e) => setChargeAmount(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="text-xs text-gray-600 mb-1 block">Disbursed date (optional)</label>
                        <input type="date" value={disbursedAt} onChange={(e) => setDisbursedAt(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>

                    <div className="mb-3">
                        <label className="text-xs text-gray-600 mb-1 block">Notes (optional)</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>

                    {preview && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
                            Total repayable <strong>{formatAmount(preview.total)}</strong>{preview.interest > 0 && <> (incl. {formatAmount(preview.interest)} interest)</>}, repaid over <strong>{preview.cycles}</strong> {chargeCycle === 'bi_weekly' ? 'bi-weekly' : 'weekly'} charges
                            {preview.last > 0 && preview.last < parseFloat(chargeAmount) && <> (last charge {formatAmount(preview.last)})</>}.
                            Projected payoff: <strong>{preview.payoff}</strong>.
                        </div>
                    )}

                    <button onClick={submitLoan} disabled={actionLoader} className="bg-[#E04343] text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Record Loan
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* Detail Modal */}
            <Modal visible={detailModal} width="560" height="520" effect="fadeInUp" onClickAway={() => setDetailModal(false)}>
                <div className="p-5 overflow-y-auto max-h-[510px]">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Loan Details</p>
                        <p className="cursor-pointer" onClick={() => setDetailModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    {!detail ? (
                        <div className="flex justify-center py-8"><Oval visible height="30" width="30" color="#E04343" secondaryColor="#eee" ariaLabel="loading" /></div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                                <div><p className="text-[10px] text-gray-400 uppercase">Rider</p><p className="text-xs font-semibold">{detail.loan.rider_name}</p><p className="text-[10px] text-gray-400">{detail.loan.rider_email}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Status</p><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${statusBadge(detail.loan.status)}`}>{detail.loan.status}</span></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Principal</p><p className="text-xs font-semibold">{formatAmount(detail.loan.principal_amount)}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Interest</p><p className="text-xs">{detail.loan.interest_rate > 0 ? `${formatAmount(detail.loan.interest_amount)} (${detail.loan.interest_rate}%)` : 'None'}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Total repayable</p><p className="text-xs font-semibold">{formatAmount(detail.loan.total_repayable)}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Charge</p><p className="text-xs">{formatAmount(detail.loan.charge_amount)} / {detail.loan.charge_cycle === 'bi_weekly' ? 'bi-weekly' : 'weekly'}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Repaid</p><p className="text-xs">{formatAmount(detail.loan.amount_repaid)} ({detail.loan.cycles_charged}/{detail.loan.total_cycles})</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Outstanding</p><p className="text-xs font-semibold">{formatAmount(detail.loan.outstanding_balance)}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Disbursed</p><p className="text-xs">{detail.loan.disbursed_at || '—'}</p></div>
                                <div><p className="text-[10px] text-gray-400 uppercase">Next charge</p><p className="text-xs">{detail.loan.next_charge_date || '—'}</p></div>
                            </div>
                            {detail.loan.notes && <div className="bg-gray-50 rounded p-2 text-xs text-gray-600 mb-3">{detail.loan.notes}</div>}
                            <p className="text-xs font-semibold text-gray-700 mb-2">Charge history</p>
                            <table className="w-full text-xs text-left border-separate border-spacing-y-1">
                                <thead className="text-[10px] text-gray-400 uppercase">
                                    <tr><th className="px-2 py-1">Cycle</th><th className="px-2 py-1">Amount</th><th className="px-2 py-1">Rider bal after</th><th className="px-2 py-1">Outstanding</th><th className="px-2 py-1">Date</th></tr>
                                </thead>
                                <tbody>
                                    {detail.charges?.length ? detail.charges.map((c: any) => (
                                        <tr key={c.id} className="bg-gray-50">
                                            <td className="px-2 py-1">{c.cycle_number}</td>
                                            <td className="px-2 py-1 font-semibold">{formatAmount(c.amount)}</td>
                                            <td className={`px-2 py-1 ${c.rider_balance_after < 0 ? 'text-red-600' : ''}`}>{formatAmount(c.rider_balance_after)}</td>
                                            <td className="px-2 py-1">{formatAmount(c.outstanding_after)}</td>
                                            <td className="px-2 py-1">{c.charged_at ? new Date(c.charged_at).toLocaleDateString() : '—'}</td>
                                        </tr>
                                    )) : <tr><td colSpan={5} className="text-center py-4 text-gray-400">No charges yet.</td></tr>}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>
            </Modal>

            {/* Edit Loan Modal */}
            <Modal visible={editModal} width="480" height="600" effect="fadeInUp" onClickAway={() => setEditModal(false)}>
                <div className="p-5 overflow-y-auto max-h-[590px]">
                    <div className="flex justify-between mb-4">
                        <p className="font-bold">Edit Loan</p>
                        <p className="cursor-pointer" onClick={() => setEditModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>

                    {editLoan && (
                        <>
                            <div className="mb-3 text-sm">
                                <p className="text-[10px] text-gray-400 uppercase">Rider</p>
                                <p className="font-semibold">{editLoan.rider_name} <span className="text-gray-400 text-xs">({editLoan.rider_email})</span></p>
                            </div>

                            {editHasCharges && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-amber-800">
                                    Repayments have already started ({editLoan.cycles_charged} charged). Loan amount and interest are locked — you can still adjust the charge amount, cycle, next charge date, and notes.
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="text-xs text-gray-600 mb-1 block">Loan amount (₦)</label>
                                <input type="number" value={editPrincipal} disabled={editHasCharges} onChange={(e) => setEditPrincipal(e.target.value)} className={`w-full border border-gray-300 rounded-md p-2 text-sm ${editHasCharges ? 'bg-gray-100 text-gray-500' : ''}`} />
                            </div>

                            <div className="mb-3">
                                <label className={`flex items-center gap-2 text-xs ${editHasCharges ? 'text-gray-400' : 'text-gray-700 cursor-pointer'}`}>
                                    <input type="checkbox" checked={editAddInterest} disabled={editHasCharges} onChange={(e) => { setEditAddInterest(e.target.checked); if (!e.target.checked) setEditInterestRate(''); }} />
                                    Add interest to this loan
                                </label>
                                {editAddInterest && (
                                    <div className="mt-2">
                                        <label className="text-xs text-gray-600 mb-1 block">Interest rate (% of loan amount)</label>
                                        <input type="number" value={editInterestRate} disabled={editHasCharges} onChange={(e) => setEditInterestRate(e.target.value)} className={`w-full border border-gray-300 rounded-md p-2 text-sm ${editHasCharges ? 'bg-gray-100 text-gray-500' : ''}`} />
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="text-xs text-gray-600 mb-1 block">Charge cycle</label>
                                    <select value={editChargeCycle} onChange={(e) => setEditChargeCycle(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                        <option value="weekly">Weekly</option>
                                        <option value="bi_weekly">Bi-weekly</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-600 mb-1 block">Amount per charge (₦)</label>
                                    <input type="number" value={editChargeAmount} onChange={(e) => setEditChargeAmount(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="text-xs text-gray-600 mb-1 block">Next charge date</label>
                                <input type="date" value={editNextChargeDate} onChange={(e) => setEditNextChargeDate(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>

                            <div className="mb-3">
                                <label className="text-xs text-gray-600 mb-1 block">Notes (optional)</label>
                                <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>

                            {editPreview && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
                                    Total repayable <strong>{formatAmount(editPreview.total)}</strong>{editPreview.interest > 0 && <> (incl. {formatAmount(editPreview.interest)} interest)</>}, over <strong>{editPreview.cycles}</strong> {editChargeCycle === 'bi_weekly' ? 'bi-weekly' : 'weekly'} charges.
                                </div>
                            )}
                            {editHasCharges && editLoan && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-xs text-gray-600">
                                    Outstanding <strong>{formatAmount(editLoan.outstanding_balance)}</strong> — at {formatAmount(parseFloat(editChargeAmount) || 0)} per charge, about <strong>{parseFloat(editChargeAmount) > 0 ? Math.ceil(Number(editLoan.outstanding_balance) / parseFloat(editChargeAmount)) : '—'}</strong> more {editChargeCycle === 'bi_weekly' ? 'bi-weekly' : 'weekly'} charges remain.
                                </div>
                            )}

                            <button onClick={submitEdit} disabled={actionLoader} className="bg-[#E04343] text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                                Save Changes
                                <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                            </button>
                        </>
                    )}
                </div>
            </Modal>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
        </>
    );
}

export default RiderLoans;
